import React, { useState, useEffect, useMemo } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  Upload, 
  FileText, 
  Download, 
  FolderOpen, 
  Calendar,
  Receipt,
  FileCheck,
  File,
  ChevronDown,
  ChevronRight,
  Filter
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'appel_fonds', label: 'Appel de fonds', icon: 'Receipt', color: 'bg-blue-100 text-blue-600' },
  { value: 'facture', label: 'Facture', icon: 'FileCheck', color: 'bg-green-100 text-green-600' },
  { value: 'pv_ag', label: 'PV AG', icon: 'FileText', color: 'bg-purple-100 text-purple-600' },
  { value: 'autre', label: 'Autre', icon: 'File', color: 'bg-slate-100 text-slate-600' }
];

const ICON_MAP = {
  'Receipt': Receipt,
  'FileCheck': FileCheck,
  'FileText': FileText,
  'File': File
};

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);

const Documents = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    nom: '',
    nature: '',
    annee: CURRENT_YEAR,
    file: null,
  });
  const [filterType, setFilterType] = useState('tous');
  const [filterYear, setFilterYear] = useState('tous');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await apiClient.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file, nom: file.name.replace(/\.[^/.]+$/, '') });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.nom || !uploadData.nature) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      try {
        await apiClient.post('/documents', {
          nom: uploadData.nom,
          type: uploadData.file.name.split('.').pop().toUpperCase(),
          nature: uploadData.nature,
          annee: uploadData.annee,
          contenu_base64: base64,
        });
        toast.success('Document ajouté au coffre');
        setUploadModal(false);
        setUploadData({ nom: '', nature: '', annee: CURRENT_YEAR, file: null });
        fetchDocuments();
      } catch (error) {
        const message = getErrorMessage(error, "Erreur lors de l'upload");
        if (message) toast.error(message);
      }
    };
    reader.readAsDataURL(uploadData.file);
  };

  const handleDownload = async (docId, nom) => {
    try {
      const response = await apiClient.get(`/documents/${docId}`);
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${response.data.contenu_base64}`;
      link.download = nom;
      link.click();
      toast.success('Document téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Filtrage et organisation des documents
  const organizedDocs = useMemo(() => {
    let filtered = documents;
    
    if (filterType !== 'tous') {
      filtered = filtered.filter(d => d.nature === filterType);
    }
    if (filterYear !== 'tous') {
      filtered = filtered.filter(d => d.annee === parseInt(filterYear));
    }

    const organized = {};
    for (let i = 0; i < filtered.length; i++) {
      const doc = filtered[i];
      const nature = doc.nature || 'autre';
      const annee = doc.annee || 'Non classé';
      
      if (!organized[nature]) {
        organized[nature] = {};
      }
      if (!organized[nature][annee]) {
        organized[nature][annee] = [];
      }
      organized[nature][annee].push(doc);
    }

    return organized;
  }, [documents, filterType, filterYear]);

  // Statistiques
  const stats = useMemo(() => {
    const byType = {};
    for (let i = 0; i < DOCUMENT_TYPES.length; i++) {
      const t = DOCUMENT_TYPES[i];
      byType[t.value] = documents.filter(d => d.nature === t.value).length;
    }
    return {
      total: documents.length,
      byType
    };
  }, [documents]);

  // Années disponibles dans les documents
  const availableYears = useMemo(() => {
    const years = new Set(documents.map(d => d.annee).filter(Boolean));
    return Array.from(years).sort((a, b) => b - a);
  }, [documents]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const renderDocumentList = (typeData, annee) => {
    const docs = typeData[annee] || [];
    return docs.map((doc) => (
      <div 
        key={doc.id}
        className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-slate-900 truncate">{doc.nom}</p>
            <p className="text-xs text-slate-500">
              {format(new Date(doc.created_at), 'dd MMM yyyy', { locale: fr })}
            </p>
          </div>
        </div>
        <Button
          onClick={() => handleDownload(doc.id, doc.nom)}
          variant="outline"
          size="sm"
          className="flex-shrink-0"
        >
          <Download className="w-4 h-4" />
        </Button>
      </div>
    ));
  };

  const renderYearSection = (typeData, years) => {
    return years.map((annee) => (
      <div key={annee} className="border-b last:border-b-0">
        <div className="px-4 py-2 bg-slate-50 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">{annee}</span>
          <span className="text-xs text-slate-400">({typeData[annee].length})</span>
        </div>
        <div className="divide-y">
          {renderDocumentList(typeData, annee)}
        </div>
      </div>
    ));
  };

  const renderCategories = () => {
    const categories = [];
    
    for (let i = 0; i < DOCUMENT_TYPES.length; i++) {
      const type = DOCUMENT_TYPES[i];
      const typeData = organizedDocs[type.value];
      if (!typeData) continue;

      const TypeIcon = ICON_MAP[type.icon];
      const years = Object.keys(typeData).sort((a, b) => b - a);
      const isExpanded = expandedCategories[type.value] !== false;
      const docCount = Object.values(typeData).flat().length;

      categories.push(
        <div key={type.value} className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <button
            onClick={() => toggleCategory(type.value)}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${type.color} rounded-lg flex items-center justify-center`}>
                <TypeIcon className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-900">{type.label}</h3>
                <p className="text-xs text-slate-500">{docCount} document(s)</p>
              </div>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {isExpanded && (
            <div className="border-t">
              {renderYearSection(typeData, years)}
            </div>
          )}
        </div>
      );
    }
    
    return categories;
  };

  return (
    <div className="fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FolderOpen className="w-7 h-7 text-amber-500" />
            Coffre documentaire
          </h1>
          <p className="text-slate-500 mt-1">
            {stats.total} document{stats.total > 1 ? 's' : ''} archivé{stats.total > 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={() => setUploadModal(true)}
          data-testid="upload-document-button"
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          <Upload className="w-4 h-4 mr-2" />
          Ajouter un document
        </Button>
      </div>

      {/* Stats par type */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {DOCUMENT_TYPES.map(type => {
          const TypeIcon = ICON_MAP[type.icon];
          const count = stats.byType[type.value] || 0;
          const isActive = filterType === type.value;
          
          return (
            <div 
              key={type.value}
              className={`bg-white rounded-xl p-4 border shadow-sm cursor-pointer transition-all hover:shadow-md ${
                isActive ? 'ring-2 ring-amber-500' : ''
              }`}
              onClick={() => setFilterType(isActive ? 'tous' : type.value)}
            >
              <div className={`w-10 h-10 ${type.color} rounded-lg flex items-center justify-center mb-2`}>
                <TypeIcon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <p className="text-xs text-slate-500">{type.label}</p>
            </div>
          );
        })}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Filtres :</span>
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
        >
          <option value="tous">Tous les types</option>
          {DOCUMENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
        >
          <option value="tous">Toutes les années</option>
          {availableYears.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        {(filterType !== 'tous' || filterYear !== 'tous') && (
          <button
            onClick={() => { setFilterType('tous'); setFilterYear('tous'); }}
            className="px-3 py-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Liste des documents organisée */}
      {Object.keys(organizedDocs).length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center">
          <FolderOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Coffre vide</h3>
          <p className="text-slate-500 mb-4">
            {filterType !== 'tous' || filterYear !== 'tous' 
              ? 'Aucun document ne correspond aux filtres'
              : 'Commencez par ajouter vos premiers documents'}
          </p>
          <Button onClick={() => setUploadModal(true)} className="bg-amber-500 hover:bg-amber-600">
            <Upload className="w-4 h-4 mr-2" />
            Ajouter un document
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {renderCategories()}
        </div>
      )}

      {/* Modal d'upload */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="max-w-md" data-testid="upload-doc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-500" />
              Ajouter un document
            </DialogTitle>
            <DialogDescription>
              Classez votre document par type et année pour le retrouver facilement.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Fichier */}
            <div>
              <Label>Fichier *</Label>
              <Input
                type="file"
                onChange={handleFileChange}
                data-testid="file-input"
                className="cursor-pointer"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              />
            </div>

            {/* Nom */}
            <div>
              <Label>Nom du document *</Label>
              <Input
                value={uploadData.nom}
                onChange={(e) => setUploadData({ ...uploadData, nom: e.target.value })}
                data-testid="doc-nom-input"
                placeholder="Ex: PV AG Janvier 2024"
              />
            </div>

            {/* Nature du document */}
            <div>
              <Label>Nature du document *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {DOCUMENT_TYPES.map(type => {
                  const TypeIcon = ICON_MAP[type.icon];
                  const isSelected = uploadData.nature === type.value;
                  
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setUploadData({ ...uploadData, nature: type.value })}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        isSelected
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`nature-${type.value}`}
                    >
                      <div className={`w-8 h-8 ${type.color} rounded flex items-center justify-center`}>
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Année */}
            <div>
              <Label>Année du document *</Label>
              <select
                value={uploadData.annee}
                onChange={(e) => setUploadData({ ...uploadData, annee: parseInt(e.target.value) })}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                data-testid="doc-annee-select"
              >
                {YEARS.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setUploadModal(false)}
                variant="outline"
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!uploadData.file || !uploadData.nom || !uploadData.nature}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                data-testid="save-doc-button"
              >
                Ajouter au coffre
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
